/**
 * VP-179: zamiana ustrukturyzowanego promptu obrazowego (JSON od LLM) na czytelne
 * dla człowieka streszczenie. JSON zostaje payloadem lecącym do modelu obrazowego,
 * a użytkownik w polu promptu widzi wyłącznie tekst.
 *
 * Skąd JSON w polu: endpoint /craft-prompt celowo prosi model o strukturę
 * (task/style/subject_rules/…), bo generatory obrazów dają na niej lepsze wyniki.
 * Ten sam string lądował w user_graphics.prompt i wracał do UI przez galerię
 * inspiracji ("Użyj").
 *
 * Realne dane z produkcji (104 wiersze) pokazały, że TYLKO 1 z nich parsuje się
 * przez samo JSON.parse — dlatego samo `try { JSON.parse } catch { return raw }`
 * wyciekłoby JSON-em w 99% przypadków. Dwa dominujące uszkodzenia:
 *  1. doklejona po klamrze linia `Output format: …` (wymuszona system promptem) — 95×,
 *  2. wstrzyknięty w środek stringa blok „Brand assets do użycia w grafice:”
 *     z surowymi newline'ami oraz proza modelu poza cudzysłowem — 8×.
 * Stąd trzy stopnie: blok zbalansowany → parse, ratunkowa ekstrakcja wartości,
 * a na końcu twarda gwarancja, że nic o kształcie JSON-a nie wraca do UI.
 */

/** Limit długości streszczenia — trzyma auto-grow textarea w rozsądnym rozmiarze. */
const MAX_SUMMARY_LENGTH = 420;

/** Klucze od najbardziej do najmniej opisowych — decydują o kolejności zdań. */
const PRIORITY_KEYS = [
  "task",
  "ad_type",
  "scene_description",
  "scene",
  "format_intent",
  "style",
  "subject",
  "subject_rules",
  "hero_product",
  "product",
  "environment",
  "environment_rules",
  "composition",
  "composition_and_lighting",
  "text_overlay",
  "typography",
  "typography_overlay",
  "layout_and_copy",
  "lighting",
  "atmosphere",
  "mood",
];

/** Klucze techniczne — nie niosą treści dla użytkownika, pomijamy je w streszczeniu. */
const SKIPPED_KEYS = new Set([
  "negative_prompt",
  "quality_directives",
  "technical",
  "output",
  "output_format",
  "format_requirements",
  "reference_image",
  "identity_lock",
]);

const SNAKE_CASE_TOKEN = /^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/**
 * Wycina pierwszy zbalansowany blok `{ … }`, ignorując klamry wewnątrz stringów.
 * W przeciwieństwie do `lastIndexOf("}")` nie daje się zwieść tekstowi doklejonemu
 * po JSON-ie (`Output format: …`).
 */
function sliceBalancedObject(text: string): string | null {
  const start = text.indexOf("{");
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i++) {
    const char = text[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === "\\") {
      escaped = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (char === "{") depth++;
    else if (char === "}") {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

/** `image_transformation` → `image transformation` (tylko dla czystych identyfikatorów). */
function humanizeToken(value: string): string {
  return SNAKE_CASE_TOKEN.test(value) ? value.replace(/_/g, " ") : value;
}

/** Zbiera stringi z dowolnie zagnieżdżonej wartości, do wyczerpania budżetu znaków. */
function collectStrings(value: unknown, collected: string[], budget: number): void {
  if (collected.join(" ").length >= budget) return;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) collected.push(trimmed);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectStrings(item, collected, budget);
    return;
  }
  if (value && typeof value === "object") {
    // Pomijanie kluczy technicznych musi działać na każdym poziomie — `negative_prompt`
    // zagnieżdżony w `style` wsadzał użytkownikowi w pole listę rzeczy, których obraz
    // ma NIE zawierać ("blurry, low quality, watermark…").
    for (const [key, nested] of Object.entries(value)) {
      if (SKIPPED_KEYS.has(key)) continue;
      collectStrings(nested, collected, budget);
    }
  }
}

function buildSummary(doc: Record<string, unknown>): string {
  const present = PRIORITY_KEYS.filter((key) => key in doc);
  const rest = Object.keys(doc).filter((key) => !PRIORITY_KEYS.includes(key));
  const seen = new Set<string>();
  const sentences: string[] = [];

  for (const key of [...present, ...rest]) {
    if (SKIPPED_KEYS.has(key)) continue;
    if (sentences.join(" ").length >= MAX_SUMMARY_LENGTH) break;

    const collected: string[] = [];
    collectStrings(doc[key], collected, MAX_SUMMARY_LENGTH);

    for (const raw of collected) {
      const sentence = humanizeToken(raw);
      const fingerprint = sentence.toLowerCase();
      if (seen.has(fingerprint)) continue;
      seen.add(fingerprint);
      sentences.push(sentence);
      if (sentences.join(" ").length >= MAX_SUMMARY_LENGTH) break;
    }
  }
  return sentences.join(". ");
}

/**
 * Ostatnia deska ratunku dla JSON-a nie do sparsowania: wyciąga same wartości
 * tekstowe par `"klucz": "wartość"`, pomijając nazwy kluczy.
 */
function salvageStringValues(text: string): string[] {
  // Próg 3 znaki, nie 12: krótkie wartości ("neon", "banner") potrafią być jedyną
  // treścią uszkodzonego promptu, a ich odrzucenie kończyło się pustym polem.
  const pattern = /"([A-Za-z0-9_ -]+)"\s*:\s*"([^"\\]{3,})"/g;
  const values: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (SKIPPED_KEYS.has(match[1])) continue;
    values.push(humanizeToken(match[2].trim()));
  }
  return values;
}

/** Czy znak na tej pozycji to pierwsza połowa pary surogatów (np. połówka emoji)? */
function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function tidy(text: string): string {
  let result = text.replace(/\s+/g, " ").trim();
  // Ciąg kropek zwijamy w jedną — wzorce nie zachodzą na siebie, więc `...` bez
  // powtórzonej grupy zostawiłoby `..`.
  result = result.replace(/\.(?:\s*\.)+/g, ".");
  if (result.length <= MAX_SUMMARY_LENGTH) return result;

  // −1 na wielokropek, żeby wynik zmieścił się w zadeklarowanym limicie.
  result = result.slice(0, MAX_SUMMARY_LENGTH - 1);
  const lastSpace = result.lastIndexOf(" ");
  if (lastSpace > MAX_SUMMARY_LENGTH * 0.6) result = result.slice(0, lastSpace);
  // Cięcie po jednostkach UTF-16 potrafi rozerwać emoji na pół i zostawić „�".
  if (result.length > 0 && isHighSurrogate(result.charCodeAt(result.length - 1)))
    result = result.slice(0, -1);
  return `${result.trimEnd()}…`;
}

/** Struktura odłożona obok pola promptu razem ze streszczeniem, które z niej powstało. */
export type HeldStructuredPrompt = { summary: string; payload: string };

/**
 * Decyduje, co realnie wysłać do generatora, na podstawie tego, co widać w polu.
 *
 * - tekst = streszczenie → pełna struktura,
 * - tekst = streszczenie + dopisek → struktura z doklejonym dopiskiem (najczęstszy ruch
 *   użytkownika; bez tego jedno dopisane słowo obcinało prompt z ~8000 do ~420 znaków),
 * - cokolwiek innego (przeróbka, skrócenie, inny prompt) → to, co widać.
 */
export function resolveStructuredPrompt(
  visible: string,
  held: HeldStructuredPrompt | null
): string {
  if (!held) return visible;
  const shown = visible.trim();
  const summary = held.summary.trim();
  if (!summary) return visible;
  if (shown === summary) return held.payload;
  if (shown.startsWith(summary)) {
    const added = shown.slice(summary.length).trim();
    return added ? `${held.payload}\n\nDodatkowe wytyczne: ${added}` : held.payload;
  }
  return visible;
}

/**
 * Czy to w ogóle kandydat na strukturę wygenerowaną przez model?
 *
 * Celowo wąskie: tekst musi ZACZYNAĆ się od klamry i zawierać parę `"klucz":`.
 * Wcześniejsza, luźniejsza wersja kasowała ludzkie prompty typu `[PILNE] reklama…`
 * czy `{SALE} — wyprzedaż` do pustego stringa. Prompt użytkownika, który tylko
 * wspomina o JSON-ie w środku zdania, ma zostać nietknięty.
 */
function isStructuredCandidate(text: string): boolean {
  return text.startsWith("{") && /"[^"\n]{1,60}"\s*:/.test(text);
}

/**
 * Usuwa z gotowego streszczenia wszystko, co zdradza strukturę. Wartości tekstowe
 * potrafią zawierać klamry (`{IMIE}`) albo cudzysłowy (`napis "SALE":`), a to trafia
 * na ekran użytkownika. Czyścimy zamiast odrzucać — odrzucenie zepchnęłoby poprawnie
 * sparsowany JSON w gorszą ścieżkę ratunkową.
 */
function stripStructuralChars(text: string): string {
  return text.replace(/[{}[\]"]/g, " ");
}

/**
 * Zwraca tekst nadający się do pokazania w polu promptu.
 * Zwykły prompt tekstowy przechodzi bez zmian; ustrukturyzowany JSON zamienia się
 * w streszczenie. Gwarancja: dla wejścia rozpoznanego jako struktura wynik nigdy
 * nie zawiera znaków struktury (w najgorszym razie jest pusty).
 */
export function summarizeImagePrompt(raw: string): string {
  if (!raw) return "";
  const text = raw.trim();
  // Nie struktura — to prompt człowieka, oddajemy go dokładnie takim, jaki był.
  if (!isStructuredCandidate(text)) return raw;

  const block = sliceBalancedObject(text);
  if (block) {
    try {
      const parsed: unknown = JSON.parse(block);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const summary = tidy(stripStructuralChars(buildSummary(parsed as Record<string, unknown>)));
        if (summary) return summary;
      }
    } catch {
      // JSON uszkodzony (proza modelu / wstrzyknięte brand assets) — ratujemy niżej.
    }
  }

  const salvaged = salvageStringValues(text);
  if (salvaged.length > 0) {
    const summary = tidy(stripStructuralChars(salvaged.join(". ")));
    if (summary) return summary;
  }
  return "";
}
